import * as sysApiTypes from '@shure/shared/angular/data-access/system-api/core';
import { AudioChannelGroup } from '@shure/shared/angular/data-access/system-api/core';
import { SignalType } from '@shure/shared/angular/data-access/system-api/models';

import {
	PowerSourceConstraintMap,
	SignalGeneratorConstraintMap,
	SignalGeneratorChannel,
	RangeContraints,
	SignalTarget
} from '../../api';
import {
	SignalGeneratorChannelFragment,
	SignalGeneratorSignalTargetChannelFragment
} from '../graphql/generated/signal-generator.generated';

interface SignalGenerator {
	signalTypes: Array<{
		signalType: sysApiTypes.SignalType;
		powerSources: Array<{
			powerSourceType: sysApiTypes.SignalGeneratorPowerSourceType;
			gainRanges: Array<{
				frequencyRange: { range: { max: number; min: number; step: number } };
				gainRange: { range: { max: number; min: number; step: number } };
			}>;
		}>;
	}>;
}

export function mapSignalGeneratorChannelFromSysApi(channel: SignalGeneratorChannelFragment): SignalGeneratorChannel {
	const {
		id,
		features,
		description: { constraints }
	} = channel;
	return {
		id,
		constraints: {
			rangeConstraintMap: mapRangeConstraints(
				constraints.signalGenerator,
				features.signalGenerator?.frequency ?? 0
			),
			signalType: {
				validSignalTypes: mapValidSignalTypes(constraints.signalGenerator) ?? []
			}
		},
		features: {
			signalGenerator: {
				enabled: features.signalGenerator?.enabled ?? false,
				frequency: features.signalGenerator?.frequency ?? 0,
				gain: features.signalGenerator?.gain ?? 0,
				signalType: features.signalGenerator?.signalType ?? SignalType.PinkNoise,
				isMissing: !channel.description.features.signalGenerator.supported
			},
			channelConfiguration: channel.description.features.channelConfiguration.supported
				? {
						enabled: channel.features.channelConfiguration?.enabled || false,
						isMissing: channel.features.channelConfiguration === null
				  }
				: undefined
		}
	};
}

export function mapSignalGeneratorSignalTargetFromSysApi(
	channel: SignalGeneratorSignalTargetChannelFragment
): SignalTarget {
	const crossPoint = channel.features.matrixMixerOutput?.crossPoints.find((crossPoint) => {
		return (
			'description' in crossPoint.inputChannel &&
			crossPoint.inputChannel.description.group === AudioChannelGroup.SignalGeneratorInput
		);
	});

	return {
		id: crossPoint?.id ?? '',
		label: channel.features.name?.name ?? '',
		enabled: !!crossPoint?.features.configuration?.enabled,
		readonly: (channel.features.channelConfiguration && !channel.features.channelConfiguration?.enabled) ?? false
	};
}

function mapRangeConstraints(signalGenerator: SignalGenerator | null, frequency: number): SignalGeneratorConstraintMap {
	if (!signalGenerator?.signalTypes) {
		return {};
	}
	const signalGeneratorConstraintMap = <SignalGeneratorConstraintMap>{};
	signalGenerator.signalTypes.forEach((signalConstraints) => {
		const powerSourceConstraintsMap = <PowerSourceConstraintMap>{};
		signalConstraints.powerSources.forEach((powerConstraint) => {
			//find gain range based on frequency range
			const frequencySectionGainRange = powerConstraint.gainRanges.find((range) => {
				return frequency >= range.frequencyRange.range.min && frequency <= range.frequencyRange.range.max;
			});
			// get frequency range:
			const minFrequency = Math.min(...powerConstraint.gainRanges.map((item) => item.frequencyRange.range.min));
			const maxFrequency = Math.max(...powerConstraint.gainRanges.map((item) => item.frequencyRange.range.max));
			const step = Math.max(...powerConstraint.gainRanges.map((item) => item.frequencyRange.range.step));

			const ranges = <RangeContraints>{
				gain: {
					range: {
						max: frequencySectionGainRange?.gainRange.range.max ?? 21,
						min: frequencySectionGainRange?.gainRange.range.min ?? -110,
						step: frequencySectionGainRange?.gainRange.range.step ?? 0.1
					}
				},
				frequency: {
					range: {
						max: maxFrequency,
						min: minFrequency,
						step
					}
				}
			};
			powerSourceConstraintsMap[powerConstraint.powerSourceType] = ranges;
		});
		signalGeneratorConstraintMap[signalConstraints.signalType] = powerSourceConstraintsMap;
	});
	return signalGeneratorConstraintMap;
}

function mapValidSignalTypes(signalGenerator: SignalGenerator | null): SignalType[] {
	if (!signalGenerator?.signalTypes) {
		return [];
	}
	return signalGenerator.signalTypes.map((signalConstraints) => signalConstraints.signalType);
}
